import React, { useCallback, useContext, useMemo, Fragment } from 'react';
import { Field, FormSpy, Form, useForm } from 'react-final-form';
import { sub } from 'date-fns';
import { pick, isEmpty } from 'lodash';
import moment from 'moment';

import inputField from 'components/Form/inputField';
import DatePickerField, { onlyTimePicker, dateTimePicker } from 'components/Form/DatePickerField';
import CoordsField from 'components/Form/CoordsField';
import { validate as validateForm, bytesToMegaBytes } from 'components/Form/utils';
import { isRequired, isValidCoordinates } from 'components/Form/validationRules';
import TextArea from 'components/Form/TextArea';
import ImagePicker, { getFileChecksum } from 'components/Form/ImagePicker';
import { presignUrl, uploadToS3 } from 'api/events';
import { useSelector } from 'react-redux';
import { NotificationAlertsContext, ALERT_TYPES } from 'components/NotificationAlerts';
import apiBase from 'api/base';
import ErrorList from 'components/Form/ErrorList';

const labels = {
  country_id: 'País',
  event_type: 'Tipo de evento',
  registration_type: 'Tipo de suscripción',
  format: 'Formato del torneo',
  sport_id: 'Deporte',
  start_date: 'Fecha de inicio',
  last_registration_date: 'Último dia de inscripción',
  place: 'Nombre del lugar',
  coordinates: 'Coordenadas',
  registration_cost: 'Costo de inscripción',
  minimum_members: 'Mínimo de integrantes',
  avatar: 'Cover / Imagen del evento',
  name: 'Nombre',
  description: 'Descripción',
  best_of: 'Gana juego el mejor de',
};

const genders = {
  female: 'Femenino',
  male: 'Masculino',
};

const genderOptions = Object.entries(genders).map(([key, value]) => ({
  [key]: value,
}));

const ProfileEditForm = ({ profile }) => {
  const { createAlert } = useContext(NotificationAlertsContext);
  const access_token = useSelector(state => state?.session?.access_token);
  const form = useForm();

  const onFileChange = useCallback(
    file => {
      form.change('avatar', '');

      if (bytesToMegaBytes(file.size) > 1) {
        createAlert('El archivo debe pesar 1MB como máximo.', ALERT_TYPES.ERROR);

        return null;
      }

      getFileChecksum(file).then(checksum => {
        const image = {
          byte_size: file.size,
          filename: file.name,
          content_type: file.type,
          checksum,
        };

        try {
          presignUrl(image).then(({ status, body }) => {
            if (status !== 200) {
              throw new Error('Presign Failed');
            }

            // Amazon requires the request to not have authorization token
            apiBase.jwt();
            uploadToS3(body, file).then(({ status: uploadStatus }) => {
              // restore token
              apiBase.jwt(access_token);

              if (uploadStatus !== 201) {
                throw new Error('Upload Failed');
              }

              form.change('avatar', body?.blob_signed_id);
            });
          });
        } catch (_) {
          // restore token
          apiBase.jwt(access_token);

          createAlert(
            'El archivo seleccionado no puede ser procesado. Porfavor intenta nuevamente. Si el error persiste recomendamos que seleccione otro archivo.',
            ALERT_TYPES.ERROR,
          );
        }
      });
    },
    [access_token, createAlert, form],
  );

  return (
    <div>
      <FormSpy
        subscription={{ touched: true, errors: true }}
        render={({ errors, touched }) => (
          <Fragment>
            {!isEmpty(errors) && touched && (
              <div className="mb-3">
                <ErrorList errors={errors} labels={labels} />
              </div>
            )}
          </Fragment>
        )}
      />
      <div className="mb-6 flex flex-wrap">
        <div className="w-full lg:w-1/2 mb-6 lg:pr-6">
          <label className="font-roboto text-xl mb-2 block" htmlFor="first_name">
            Nombre
          </label>
          <Field
            name="first_name"
            component="input"
            type="text"
            placeholder="Nombre"
            className="border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg"
          />
        </div>
        <div className="w-full lg:w-1/2 mb-6">
          <label className="font-roboto text-xl mb-2 block" htmlFor="last_name">
            Apellido
          </label>
          <Field
            name="last_name"
            component="input"
            type="text"
            placeholder="Apellido"
            className="border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg"
          />
        </div>
      </div>
      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block">Foto de perfil</label>
        <Field
          name="cover_file"
          key="cover-file"
          component={inputField(ImagePicker)}
          onChange={onFileChange}
          inputProps={{ avatar_url: profile?.avatar_url }}
        />
      </div>
      <div className="mb-6">
        <label className="font-roboto text-xl mb-2 block" htmlFor="user_name">
          Nombre de usuario
        </label>
        <Field
          name="user_name"
          component="input"
          placeholder="Nombre de usuario"
          className="border-brandblue border-solid border rounded-lg py-2 px-4 w-full text-lg"
        />
      </div>

      <div className="mb-6 flex flex-wrap">
        <div className="w-full lg:w-1/2 mb-6 lg:pr-6">
          <label className="font-roboto text-xl mb-2 block">Fecha de nacimiento</label>
          <Field
            name="date_of_birth"
            component={inputField(DatePickerField)}
            inputProps={{
              placeholder: 'Fecha de nacimiento',
              minDate: moment('1900-01-01').toDate(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProfileEditForm);
